import {
  Box,
  Button,
  Card,
  CardContent,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useCan } from "../../../auth/permissions.js";
import { ConfirmDialog } from "../../../components/ui/confirm-dialog.js";
import { toast } from "../../../components/ui/use-toast.js";
import { ApiError } from "../../../lib/api.js";
import { PageLoader } from "@/components/ui/page-loader";
import { SectionCardHeader, PlatformPill } from "../shared.js";
import { marketingApi } from "../client.js";
import type { Platform, PlatformAccount } from "../types.js";
import { ConnectPlatformDialog } from "./ConnectPlatformDialog.js";

// Surface the upstream's real reason instead of failing silently — a silent
// connect/test/toggle/disconnect was the "social settings don't work" report.
const errMsg = (e: unknown): string =>
  e instanceof ApiError ? e.message : "Something went wrong — please retry.";

const PLATFORMS_KEY = ["marketing", "platforms"] as const;

// Only the single-token adapters can publish from a pasted token; reddit
// (4-credential OAuth) and the safe-mode stubs post manually, so we don't
// offer a misleading "connect" for them.
const CONNECTABLE: Platform[] = [
  "linkedin_post",
  "twitter_thread",
  "threads_post",
  "facebook_post",
];

function AccountStatusPill({ status }: { status: string | null }) {
  const sx =
    status === "connected"
      ? { bgcolor: (t: import("@mui/material/styles").Theme) => alpha(t.palette.success.main, 0.16), color: "success.main" }
      : status === "error"
        ? { bgcolor: (t: import("@mui/material/styles").Theme) => alpha(t.palette.error.main, 0.16), color: "error.main" }
        : status === "safe_mode"
          ? { bgcolor: (t: import("@mui/material/styles").Theme) => alpha(t.palette.warning.main, 0.16), color: "warning.main" }
          : { bgcolor: "action.hover", color: "text.secondary" };
  return (
    <Box
      component="span"
      sx={{ borderRadius: 999, px: 1, py: 0.25, fontSize: 11, fontWeight: 500, ...sx }}
    >
      {status ? status.replace(/_/g, " ") : "not connected"}
    </Box>
  );
}

export function PlatformAccountsPanel(): JSX.Element {
  const qc = useQueryClient();
  // Connecting/re-keying/testing/disconnecting accounts + the per-account
  // auto-publish toggle are "Manage Marketing settings" (marketing:manage),
  // NOT marketing:edit. A marketing_manager (edit, no manage) sees status but
  // gets no controls — the server enforces the same wall.
  const canEdit = useCan("marketing:manage");
  const accounts = useQuery({
    queryKey: PLATFORMS_KEY,
    queryFn: marketingApi.listPlatformAccounts,
    retry: 0,
  });

  const [connectFor, setConnectFor] = useState<Platform | null>(null);
  const [disconnectId, setDisconnectId] = useState<number | null>(null);

  const byPlatform = new Map<string, PlatformAccount>();
  for (const a of accounts.data ?? []) byPlatform.set(a.platform, a);

  // The account behind the open connect dialog. A disconnected account is
  // "not linked" — its stale name must not be resurrected as a prefill.
  const connectForAcct = connectFor ? byPlatform.get(connectFor) ?? null : null;
  const connectForLinked = !!connectForAcct && connectForAcct.status !== "disconnected";

  const invalidate = () => void qc.invalidateQueries({ queryKey: PLATFORMS_KEY });

  const connect = useMutation({
    mutationFn: (v: { platform: Platform; account_name: string; token: string }) =>
      marketingApi.connectPlatform(v),
    // Errors are surfaced inline by ConnectPlatformDialog (it awaits the
    // mutation); here we just confirm success + refresh.
    onSuccess: (acct: PlatformAccount) => {
      invalidate();
      toast({ title: "Account connected", description: `${labelFor(acct.platform)} is ready.` });
    },
  });
  const test = useMutation({
    mutationFn: (id: number) => marketingApi.testPlatform(id),
    onSuccess: (acct: PlatformAccount) => {
      invalidate();
      if (acct.status === "connected") {
        toast({ title: "Token works", description: `${labelFor(acct.platform)} is connected.` });
      } else {
        toast({
          variant: "destructive",
          title: "Token check failed",
          description: acct.last_check_error ?? "The platform rejected the token.",
        });
      }
    },
    onError: (e) =>
      toast({ variant: "destructive", title: "Test failed", description: errMsg(e) }),
  });
  const togglePublish = useMutation({
    mutationFn: (v: { id: number; enabled: boolean }) =>
      marketingApi.setPlatformPublish(v.id, v.enabled),
    onSuccess: (acct: PlatformAccount) => {
      invalidate();
      toast({ title: acct.publish_enabled ? "Auto-publish on" : "Auto-publish off" });
    },
    onError: (e) =>
      toast({ variant: "destructive", title: "Couldn't update", description: errMsg(e) }),
  });
  const disconnect = useMutation({
    mutationFn: (id: number) => marketingApi.disconnectPlatformAccount(id),
    onSuccess: () => {
      invalidate();
      toast({ title: "Disconnected" });
    },
    onError: (e) =>
      toast({ variant: "destructive", title: "Couldn't disconnect", description: errMsg(e) }),
  });

  return (
    <Card>
      <SectionCardHeader
        title="Social accounts"
        subtitle="Connect the accounts the agent can post to. Tokens are encrypted at rest."
      />
      <CardContent sx={{ px: 2.5, py: 2 }}>
        {accounts.isLoading ? (
          <PageLoader />
        ) : (
          <Box sx={{ mx: -0.5, overflowX: "auto" }} data-testid="platform-accounts">
            <Table size="small" sx={{ minWidth: "100%" }}>
              <TableHead>
                <TableRow>
                  <Th>Platform</Th>
                  <Th>Account</Th>
                  <Th>Status</Th>
                  <Th>Token</Th>
                  <Th align="center">Auto-publish</Th>
                  {canEdit ? <Th align="right">Actions</Th> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {CONNECTABLE.map((platform) => {
                  const acct = byPlatform.get(platform) ?? null;
                  const connected = acct?.status === "connected";
                  // Disconnect is a soft-clear upstream: it nulls the token but
                  // KEEPS the row + account_name. Treat a disconnected account
                  // as not-linked so the row collapses back to the pristine
                  // "not connected" state (no stale name, no Test/Disconnect) —
                  // otherwise a disconnect looks like a no-op ("it says
                  // disconnected but nothing is disconnected").
                  const linked = !!acct && acct.status !== "disconnected";
                  return (
                    <TableRow
                      key={platform}
                      data-testid={`platform-row-${platform}`}
                      sx={{ "& > td": { borderColor: "divider" }, "&:last-of-type > td": { borderBottom: 0 } }}
                    >
                      <TableCell sx={{ px: 1, py: 1, verticalAlign: "middle" }}>
                        <PlatformPill platform={platform} />
                      </TableCell>
                      <TableCell sx={{ px: 1, py: 1, verticalAlign: "middle", color: "text.primary" }}>
                        {linked ? (
                          acct.account_name
                        ) : (
                          <Box component="span" sx={{ color: "text.disabled" }}>—</Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ px: 1, py: 1, verticalAlign: "middle" }}>
                        <AccountStatusPill status={acct?.status ?? null} />
                      </TableCell>
                      <TableCell sx={{ px: 1, py: 1, verticalAlign: "middle", fontFamily: "ui-monospace, monospace", fontSize: 12, color: "text.disabled" }}>
                        {acct?.credential_hint ? `…${acct.credential_hint}` : "—"}
                      </TableCell>
                      <TableCell align="center" sx={{ px: 1, py: 1, verticalAlign: "middle" }}>
                        <Switch
                          size="small"
                          checked={!!acct?.publish_enabled}
                          disabled={!canEdit || !acct || !connected || togglePublish.isPending}
                          onChange={(_, checked) =>
                            acct && togglePublish.mutate({ id: acct.id, enabled: checked })
                          }
                          inputProps={{ "aria-label": `Auto-publish ${platform}` }}
                        />
                      </TableCell>
                      {canEdit ? (
                        <TableCell sx={{ px: 1, py: 1, verticalAlign: "middle" }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                            <Button
                              variant="outlined"
                              color="inherit"
                              size="small"
                              onClick={() => setConnectFor(platform)}
                            >
                              {acct?.credential_hint ? "Re-key" : "Connect"}
                            </Button>
                            {linked ? (
                              <>
                                <Button
                                  variant="outlined"
                                  color="inherit"
                                  size="small"
                                  disabled={test.isPending}
                                  onClick={() => test.mutate(acct.id)}
                                >
                                  Test
                                </Button>
                                <Button
                                  variant="text"
                                  color="inherit"
                                  size="small"
                                  onClick={() => setDisconnectId(acct.id)}
                                >
                                  Disconnect
                                </Button>
                              </>
                            ) : null}
                          </Box>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Typography sx={{ mt: 1.5, fontSize: 12, color: "text.disabled" }}>
              Reddit, Instagram, YouTube, blog, press releases, video scripts and
              email don't auto-publish from a pasted token — they're posted
              manually.
            </Typography>
          </Box>
        )}
      </CardContent>

      <ConnectPlatformDialog
        isOpen={connectFor !== null}
        onClose={() => setConnectFor(null)}
        platformLabel={connectFor ? labelFor(connectFor) : ""}
        initialAccountName={
          // A disconnected account is treated as not-linked, so don't
          // pre-fill its stale name — the connect dialog opens blank.
          connectForLinked ? connectForAcct!.account_name : ""
        }
        hasExistingCredential={connectForLinked ? !!connectForAcct!.credential_hint : false}
        onSave={async (account_name, token) => {
          if (connectFor) {
            await connect.mutateAsync({ platform: connectFor, account_name, token });
          }
        }}
      />

      <ConfirmDialog
        isOpen={disconnectId !== null}
        onClose={() => setDisconnectId(null)}
        title="Disconnect this account?"
        description="This clears the stored token and stops autonomous posting for the account. You can reconnect later."
        confirmLabel="Disconnect"
        variant="destructive"
        onConfirm={async () => {
          if (disconnectId !== null) await disconnect.mutateAsync(disconnectId);
        }}
      />
    </Card>
  );
}

function labelFor(platform: Platform): string {
  return (
    {
      linkedin_post: "LinkedIn",
      twitter_thread: "X",
      threads_post: "Threads",
      facebook_post: "Facebook",
    } as Record<string, string>
  )[platform] ?? platform;
}

function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right" | "center";
}) {
  return (
    <TableCell
      align={align}
      sx={{
        px: 1,
        py: 1,
        borderColor: "divider",
        fontSize: 10,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.14em",
        color: "text.disabled",
      }}
    >
      {children}
    </TableCell>
  );
}
